<template lang="pug">
  .bar.topbar(:style='barStyle')
    .m-sidebar-toggle(v-if="screenLessThan1100" @click='toggleSidenav' :style="toggleStyle")
      icon(name="bars")

    .m-right-sidebar-toggle(v-if="screenLessThan1100" @click='toggleRightSidenav' :style="toggleStyle")
      icon(name="th")

    .side(v-if="!screenLessThan1100")
      .company

        company-switcher(v-if='showCompanySwitcher')
      .apps
        router-link.app.special(v-if='isSuperUser' key='superuser' :to='{ name: "superAdmin:companyList" }' :class='{ active: isSuperAdminAppActive }')
          .app-name SuperAdmin

        router-link.app.special(v-if='showCompanyAdminApp' key='companyAdmin' :to='{ name: "companyAdmin:dashboard" }' :class='{ active: isCompanyAdminAppActive }')
          .app-name Company Admin

        router-link.app(:to='app.linkTo' v-if='activeCompany' v-for='app in allowedAppList' :key='app.label' active-class='active')
          .app-icon
            icon(:name='app.icon')
          .app-name {{ app.label }}

      el-popover(placement="bottom" width="250" trigger="click")
        .user(slot="reference")
          icon(name="user" scale="1.5")
        user-popup
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import chroma from "chroma-js";
import thumbUrl from "@/shared/utils/thumbUrl";

import UserPopup from "@/components/layout/UserPopup";
import CompanySwitcher from "@/components/layout/CompanySwitcher";

export default {
  components: { UserPopup, CompanySwitcher },
  data() {
    return {
      logoutLoading: false,
      alwaysAllowedApps: ["spage"],
      apps: [
        {
          label: "sPage",
          icon: "bullhorn",
          linkTo: { name: "spage:panelList", params: { app: "spage" } }
        },
        {
          label: "BrApp",
          icon: "sliders-h",
          linkTo: { name: "brapp:dashboard", params: { app: "brapp" } }
        },
        {
          label: "Insight",
          icon: "lightbulb",
          linkTo: { name: "insight:dashboard", params: { app: "insight" } }
        },
        {
          label: "iTrain",
          icon: "star",
          linkTo: { name: "itrain:dashboard", params: { app: "itrain" } }
        }
      ]
    };
  },
  computed: {
    ...mapGetters("auth", [
      "user",
      "isSuperUser",
      "allowedApps",
      "isActiveCompanyAdmin",
      "activeCompany"
    ]),
    ...mapGetters("ui", [
      "colorScheme",
      "sidenavCollapsed",
      "rightSideNavCollapsed"
    ]),
    sidebarsClosed() {
      return this.rightSideNavCollapsed && this.sidenavCollapsed;
    },
    showCompanyAdminApp() {
      return (
        this.activeCompany && (this.isSuperUser || this.isActiveCompanyAdmin)
      );
    },
    isCompanyAdminAppActive() {
      return this.$route.path.startsWith("/company-admin");
    },
    isSuperAdminAppActive() {
      return this.$route.path.startsWith("/super-admin");
    },
    allowedAppList() {
      if (this.allowedApps === "*") {
        return this.apps;
      }
      if (!this.allowedApps.length) {
        return [];
      }
      return this.apps.filter(app => {
        return (
          this.alwaysAllowedApps.includes(app.codename) ||
          !!this.allowedApps.find(a => a.codename === app.codename)
        );
      });
    },
    showCompanySwitcher() {
      return this.user && this.isSuperUser;
    },
    barStyle() {
      const hasCompanyLogo = this.activeCompany && this.activeCompany.logoUrl;

      return {
        backgroundColor: this.colorScheme.primaryColour,
        backgroundImage: hasCompanyLogo
          ? `url(${thumbUrl(this.activeCompany.logoUrl, 200, 50)})`
          : undefined,
        backgroundSize: hasCompanyLogo ? "auto" : undefined
      };
    },
    logoStyle() {
      return {
        backgroundColor: chroma(this.colorScheme.secondaryColour)
          .darken(0.5)
          .hex()
      };
    },
    toggleStyle() {
      return {
        backgroundColor: chroma(this.colorScheme.primaryColour)
          .darken(0.5)
          .hex()
      };
    },
    screenLessThan1100() {
      return this.$mq.resize && this.$mq.below(1100);
    },
    screenLessThan400() {
      return this.$mq.resize && this.$mq.below(400);
    }
  },
  methods: {
    ...mapMutations("ui", ["toggleSidenav", "toggleRightSidenav"]),
    ...mapMutations({
      setActiveCompany: "auth/setActiveCompany"
    }),
    appClass(appName) {
      return {
        active: this.$route.params.app === appName
      };
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/mixins.scss";
.bar {
  flex-basis: 60px;
  display: flex;
  align-items: center;
  background-color: #25c4cd;
  color: white;
  justify-content: space-between;
  transition: background-color 250ms ease-in-out, border-color 250ms ease-in-out;
  background-position: 2%;
  @include MQ(LG) {
    background-position: 50%;
  }
}

.m-sidebar-toggle,
.m-right-sidebar-toggle {
  flex-basis: 82px;
  max-width: 82px;
  padding: 19px 30px;
}
.m-sidebar-toggle {
  // margin-right: auto;
}

.menu-icon {
  margin-right: 10px;
}

.side {
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  align-self: stretch;
}

.company {
  margin-right: 30px;
  display: flex;
  align-items: center;
}

.apps {
  display: flex;
  align-items: center;
  align-self: stretch;
}

.app {
  display: flex;
  flex-direction: column;
  border-left: 1px solid lighten(#25c4cd, 8%);
  transition: background-color 250ms ease-in-out, border-color 250ms ease-in-out;
  align-self: stretch;
  padding: 10px;
  width: 70px;
  cursor: pointer;
  color: #333 !important;
  font-family: "gilroyextrabold";
  font-weight: bold;
  &:last-child {
    border-right: 1px solid lighten(#25c4cd, 8%);
  }

  &.active {
    background: #1c9ea5;
  }

  &.special {
    width: auto;
    justify-content: center;
  }
}

.app-icon {
  flex: 1;
  text-align: center;
}

.app-name {
  flex: 0 0 auto;
  text-align: center;
  font-size: 12px;
}

.user {
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: 20px 30px;
  // color:white;
  font-family: "gilroyextrabold";
  font-weight: bold;
}

.user-name {
  font-size: 15px;
}

.user-actions {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
}
</style>
